/** `marketing` — promo tasks on a calendar: posts, newsletters, press. */
import { z } from "zod";
import { defineCollection, field } from "../define.ts";

const DAY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD");
const WALL_TIME = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "expected HH:MM");

export const marketing = defineCollection({
  key: "marketing",
  labelKey: "collections.marketing",
  icon: "megaphone",
  sheetTab: "Marketing",
  dedupFields: ["day", "channel", "task"],
  sort: [{ field: "day" }, { field: "time" }],
  split: "gig",
  chatHint:
    "Marketing/promo tasks with dates: Instagram/TikTok posts, newsletter sends, press, " +
    "Spotify pushes, website updates. 'Post the flyer Friday' creates a row here.",
  fields: {
    task: field({
      type: z.string().min(1),
      tier: "hot",
      pii: "crew",
      labelKey: "marketing.task",
      sheet: { column: "Task" },
      description: "What to do (post flyer, send newsletter, announce lineup…).",
    }),
    channel: field({
      type: z.string().optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "marketing.channel",
      suggestedChips: ["Instagram", "TikTok", "Facebook", "Newsletter", "Spotify", "Press", "Website"],
      sheet: { column: "Channel" },
      description: "Instagram | TikTok | Facebook | Newsletter | Spotify | Press | Website | …",
    }),
    day: field({
      type: DAY.optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "marketing.day",
      renamedFrom: ["date"],
      sheet: { column: "Day" },
      description: "When it should happen (YYYY-MM-DD).",
    }),
    time: field({
      type: WALL_TIME.optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "marketing.time",
      sheet: { column: "Time" },
      description: "Time of day (HH:MM) when relevant.",
    }),
    status: field({
      type: z.enum(["todo", "scheduled", "done"]).optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "marketing.status",
      sheet: { column: "Status" },
      description: "todo | scheduled | done.",
    }),
    url: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "marketing.url",
      sheet: { column: "URL" },
      description: "Link to the published post/asset.",
    }),
    notes: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "marketing.notes",
      sheet: { column: "Notes" },
      description: "Copy drafts, tags, coordination notes.",
    }),
  },
});
